import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api, type GeoCountry, type GeoCurrency } from "@/api";
import { geoKeys, useGeoCountries, useGeoCurrencies } from "@/api/hooks/use-geo";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";

// Geo settings editor — the single source of truth for supported markets and
// display currencies. The storefront location picker and the phone country
// lists read this (the public surface filters to enabled countries). Saving
// PATCHes the gateway; changes are live immediately.
export function GeoSettingsPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data: countriesData,
    error: countriesError
  } = useGeoCountries();
  const {
    data: currenciesData,
    error: currenciesError
  } = useGeoCurrencies();
  const [countries, setCountries] = useState<GeoCountry[] | null>(null);
  const [currencies, setCurrencies] = useState<GeoCurrency[] | null>(null);
  const [original, setOriginal] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Сырой текст поля «Города» по индексу страны. Раньше поле пересобиралось из
  // массива на каждом нажатии, и запятая тут же съедалась — добавить город
  // руками было нельзя (только вставкой готовой строки).
  const [cityDrafts, setCityDrafts] = useState<Record<number, string>>({});

  // Seed the editable lists once, when both queries have data; background
  // refetches must not clobber in-progress edits.
  useEffect(() => {
    if (!countriesData || !currenciesData) return;
    setCountries(cs => cs ?? countriesData);
    setCurrencies(cur => cur ?? currenciesData);
    setOriginal(o => o || JSON.stringify({
      c: countriesData,
      cur: currenciesData
    }));
  }, [countriesData, currenciesData]);
  const loadError = countriesError ?? currenciesError;
  // Ошибка сохранения показывается рядом с кнопкой, а не вместо страницы:
  // иначе оператор терял все несохранённые правки.
  const error = loadError ? loadError.message : null;
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (!countries || !currencies) return <p className="text-muted-foreground">{t("geo.loading")}</p>;
  const dirty = JSON.stringify({
    c: countries,
    cur: currencies
  }) !== original;
  const setCountry = (i: number, patch: Partial<GeoCountry>) => setCountries(cs_0 => cs_0?.map((c, idx) => idx === i ? {
    ...c,
    ...patch
  } : c) ?? cs_0);
  const removeCountry = (i_0: number) => {
    setCountries(cs_1 => cs_1?.filter((_, idx_0) => idx_0 !== i_0) ?? cs_1);
    setCityDrafts({});
  };
  const parseCities = (text: string) => [...new Set(text.split(/[,\n;]/).map(city => city.trim()).filter(Boolean))];
  const addCountry = () => setCountries(cs_2 => [...(cs_2 ?? []), {
    code: "",
    name: "",
    currency: currencies[0]?.code ?? "RUB",
    cities: [],
    enabled: true
  }]);
  const setCurrency = (i_1: number, patch_0: Partial<GeoCurrency>) => setCurrencies(cur_0 => cur_0?.map((c_0, idx_1) => idx_1 === i_1 ? {
    ...c_0,
    ...patch_0
  } : c_0) ?? cur_0);
  const removeCurrency = (i_2: number) => setCurrencies(cur_1 => cur_1?.filter((__0, idx_2) => idx_2 !== i_2) ?? cur_1);
  const addCurrency = () => setCurrencies(cur_2 => [...(cur_2 ?? []), {
    code: "",
    symbol: "",
    name: "",
    rate: 1,
    symbolBefore: false
  }]);
  const save = async () => {
    setSaving(true);
    setActionError(null);
    try {
      const [c_1, cur_3] = await Promise.all([api.geo.updateCountries(countries.map(x => ({
        ...x,
        code: x.code.trim().toUpperCase()
      }))), api.geo.updateCurrencies(currencies.map(x_0 => ({
        ...x_0,
        code: x_0.code.trim().toUpperCase()
      })))]);
      queryClient.setQueryData(geoKeys.countries, c_1);
      queryClient.setQueryData(geoKeys.currencies, cur_3);
      setCountries(c_1);
      setCurrencies(cur_3);
      setOriginal(JSON.stringify({
        c: c_1,
        cur: cur_3
      }));
      setCityDrafts({});
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  return <div className="flex max-w-4xl flex-col gap-6">
			<div className="flex flex-col gap-1">
				<h1 className="text-2xl font-bold">{t("nav.geo")}</h1>
				<p className="text-sm text-muted-foreground">{t("geo.intro")}</p>
			</div>

			{/* Countries */}
			<section className="flex flex-col gap-4 rounded-lg border p-5">
				<h2 className="text-sm font-semibold uppercase text-muted-foreground">
					{t("geo.countriesHeading")}
				</h2>
				<div className="flex flex-col gap-4">
					{countries.map((c_2, i_3) => <div key={i_3} className="grid items-end gap-3 rounded-md border p-3 sm:grid-cols-[80px_1fr_120px_auto]">
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.code")}</Label>
								<Input value={c_2.code} maxLength={2} onChange={e_0 => setCountry(i_3, {
              code: e_0.target.value
            })} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.name")}</Label>
								<Input value={c_2.name} onChange={e_1 => setCountry(i_3, {
              name: e_1.target.value
            })} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.currency")}</Label>
								<select className="h-9 rounded-md border bg-transparent px-2 text-sm shadow-xs" value={c_2.currency} onChange={e_2 => setCountry(i_3, {
              currency: e_2.target.value
            })}>
									{currencies.map(cur_4 => <option key={cur_4.code} value={cur_4.code}>
											{cur_4.code}
										</option>)}
								</select>
							</div>
							<div className="flex items-center gap-2 pb-0.5">
								<Button type="button" variant={c_2.enabled ? "default" : "outline"} size="sm" onClick={() => setCountry(i_3, {
              enabled: !c_2.enabled
            })}>
									{c_2.enabled ? t("geo.enabled") : t("geo.disabled")}
								</Button>
								<Button type="button" variant="ghost" size="sm" onClick={() => removeCountry(i_3)} aria-label={t("geo.remove")}>
									<Trash2 className="size-4" />
								</Button>
							</div>
							<div className="flex flex-col gap-1.5 sm:col-span-4">
								<Label htmlFor={`geo-cities-${i_3}`}>
									{t("geo.cities")} · {c_2.cities.length}
								</Label>
								<textarea id={`geo-cities-${i_3}`} data-testid={`geo-cities-${c_2.code || i_3}`} className="min-h-16 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50" value={cityDrafts[i_3] ?? c_2.cities.join(", ")} placeholder={t("geo.citiesHint")} onChange={e_3 => {
              const text_0 = e_3.target.value;
              setCityDrafts(d => ({
                ...d,
                [i_3]: text_0
              }));
              setCountry(i_3, {
                cities: parseCities(text_0)
              });
            }} />
							</div>
						</div>)}
				</div>
				<Button type="button" variant="outline" size="sm" className="self-start" onClick={addCountry}>
					<Plus className="size-4" /> {t("geo.addCountry")}
				</Button>
			</section>

			{/* Currencies */}
			<section className="flex flex-col gap-4 rounded-lg border p-5">
				<h2 className="text-sm font-semibold uppercase text-muted-foreground">
					{t("geo.currenciesHeading")}
				</h2>
				<div className="flex flex-col gap-3">
					{currencies.map((c_3, i_4) => <div key={i_4} className="grid items-end gap-3 rounded-md border p-3 sm:grid-cols-[90px_80px_1fr_110px_auto]">
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.code")}</Label>
								<Input value={c_3.code} onChange={e_4 => setCurrency(i_4, {
              code: e_4.target.value
            })} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.symbol")}</Label>
								<Input value={c_3.symbol} onChange={e_5 => setCurrency(i_4, {
              symbol: e_5.target.value
            })} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.name")}</Label>
								<Input value={c_3.name} onChange={e_6 => setCurrency(i_4, {
              name: e_6.target.value
            })} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>{t("geo.rate")}</Label>
								<Input type="number" step="any" min={0} value={String(c_3.rate)} onChange={e_7 => setCurrency(i_4, {
              rate: Number(e_7.target.value) || 0
            })} />
							</div>
							<div className="flex items-center gap-2 pb-0.5">
								<Button type="button" variant={c_3.symbolBefore ? "default" : "outline"} size="sm" onClick={() => setCurrency(i_4, {
              symbolBefore: !c_3.symbolBefore
            })} title={t("geo.symbolBeforeHint")}>
									{c_3.symbolBefore ? `${c_3.symbol}10` : `10${c_3.symbol}`}
								</Button>
								<Button type="button" variant="ghost" size="sm" onClick={() => removeCurrency(i_4)} aria-label={t("geo.remove")}>
									<Trash2 className="size-4" />
								</Button>
							</div>
						</div>)}
				</div>
				<Button type="button" variant="outline" size="sm" className="self-start" onClick={addCurrency}>
					<Plus className="size-4" /> {t("geo.addCurrency")}
				</Button>
			</section>

			{actionError && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
					{actionError}
				</div>}
			<div className="flex items-center gap-3">
				<Button onClick={save} disabled={saving || !dirty}>
					{saving ? t("geo.saving") : t("geo.save")}
				</Button>
				{savedAt && !dirty && <span className="text-sm text-muted-foreground">{t("geo.saved")}</span>}
			</div>
		</div>;
}